'use client';

import { useMemo, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { getStorage, useLocalStorage } from 'src/hooks/use-local-storage';

import { getGroupedProductsRequest } from 'src/api/menloshops';

import { ICheckoutItem } from 'src/types/checkout';

import { CheckoutContext } from './checkout-context';

const PRODUCT_CHECKOUT_STEPS = ['Cart', 'Done'];

const STORAGE_KEY = 'checkout';

const initialState = {
  activeStep: 0,
  items: [],
  subTotal: 0,
  total: 0,
  totalItems: 0,
  groupedProducts: [],
};

type Props = {
  children: React.ReactNode;
};

export function CheckoutProvider({ children }: Readonly<Props>) {
  const router = useRouter();

  const { state, update, reset } = useLocalStorage(STORAGE_KEY, initialState);

  const onGetCart = useCallback(() => {
    const totalItems: number = state?.groupedProducts?.reduce(
      (total: any, supplier: { products: string | any[] }) =>
        total +
        (Array.isArray(supplier?.products)
          ? supplier.products.reduce((sum: number, product: any) => sum + product.quantity, 0)
          : 0),
      0
    );

    update('totalItems', totalItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.groupedProducts]);

  useEffect(() => {
    const restored = getStorage(STORAGE_KEY);

    if (restored) {
      onGetCart();
    }
  }, [onGetCart]);

  const onAddToCart = useCallback(
    async (newItem: ICheckoutItem) => {
      const newState = [...state.groupedProducts];
      const existingSupplierIndex = newState.findIndex(
        (item) => item?.supplier.id === newItem.supplier.id
      );

      let payload = [];

      if (existingSupplierIndex !== -1) {
        const existingProductIndex = newState[existingSupplierIndex].products.findIndex(
          (product: { id: string }) => product.id === newItem.productId
        );
        if (existingProductIndex !== -1) {
          payload = newState[existingSupplierIndex].products.map(
            (product: { id: string; quantity: number }) => ({
              id: product.id,
              quantity:
                product.id === newItem.productId
                  ? product.quantity + newItem.quantity
                  : product.quantity,
            })
          );
        } else {
          payload = newState[existingSupplierIndex].products.concat({
            id: newItem.productId,
            quantity: newItem.quantity,
          });
        }
      } else {
        payload.push({
          id: newItem.productId,
          quantity: newItem.quantity,
        });
      }

      const groupedProductResponse = await getGroupedProductsRequest(payload, {
        supplierId: newItem.supplier.id,
      });

      if (!groupedProductResponse) return;

      const newProduct = groupedProductResponse[0];

      if (existingSupplierIndex !== -1) {
        newState[existingSupplierIndex].products = newProduct.products;
        newState[existingSupplierIndex].supplier = newProduct.supplier;
        newState[existingSupplierIndex].paymentOptions = newProduct.paymentOptions;
        newState[existingSupplierIndex].defaultPayment = newProduct.defaultPayment;
      } else {
        newState.push(newProduct);
      }

      update(
        'groupedProducts',
        newState.filter((item) => item !== null)
      );
    },
    [state.groupedProducts, update]
  );

  const onBackStep = useCallback(() => {
    update('activeStep', state.activeStep - 1);
  }, [update, state.activeStep]);

  const onNextStep = useCallback(() => {
    update('activeStep', state.activeStep + 1);
  }, [update, state.activeStep]);

  const onGotoStep = useCallback(
    (step: number) => {
      update('activeStep', step);
    },
    [update]
  );

  const onSaveGroupProducts = useCallback(
    async (groupedProducts: any) => {
      update('groupedProducts', groupedProducts);
    },
    [update]
  );

  const completed = state.activeStep === PRODUCT_CHECKOUT_STEPS.length;

  // Reset
  const onReset = useCallback(() => {
    if (completed) {
      reset();
      router.replace(paths.menloShops.products.root);
    }
  }, [completed, reset, router]);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      completed,
      //
      onAddToCart,
      //
      onBackStep,
      onNextStep,
      onGotoStep,
      //
      onReset,
      onSaveGroupProducts,
    }),
    [
      state,
      completed,
      onAddToCart,
      onBackStep,
      onNextStep,
      onGotoStep,
      onReset,
      onSaveGroupProducts,
    ]
  );

  return <CheckoutContext.Provider value={memoizedValue}>{children}</CheckoutContext.Provider>;
}
